/**
 * AURELIA — component-video.js
 *
 * <video-facade>  Replaces a poster image with the provider iframe on click.
 *                 Saves 500 KB–1 MB of third-party JavaScript per embed until
 *                 the visitor actually asks for the video.
 *
 * <video-section> Plays / pauses a native <video> when it scrolls in and out
 *                 of view, so an off-screen background video never decodes
 *                 frames or drains battery.
 */
(function () {
  'use strict';

  const { Element, motion } = window.Aurelia;

  /* ---------------------------------------------------------------------
     Facade
     --------------------------------------------------------------------- */
  class VideoFacade extends Element {
    mount() {
      this.button = this.querySelector('.video__play');
      if (!this.button) return;

      this.listen(this.button, 'click', () => this.activate());

      // Warm the provider's connection on hover so the iframe starts sooner
      // once the visitor commits. Costs one DNS + TLS handshake, no payload.
      this.listen(this, 'pointerenter', () => this.preconnect(), { once: true });
      this.listen(this, 'focusin', () => this.preconnect(), { once: true });
    }

    preconnect() {
      const origins =
        this.dataset.provider === 'vimeo'
          ? ['https://player.vimeo.com', 'https://i.vimeocdn.com']
          : ['https://www.youtube-nocookie.com', 'https://i.ytimg.com'];

      origins.forEach((origin) => {
        if (document.querySelector(`link[rel="preconnect"][href="${origin}"]`)) return;
        const link = document.createElement('link');
        link.rel = 'preconnect';
        link.href = origin;
        link.crossOrigin = '';
        document.head.appendChild(link);
      });
    }

    activate() {
      if (this.activated) return;
      this.activated = true;

      const iframe = document.createElement('iframe');
      iframe.className = 'video__iframe';
      iframe.src = this.dataset.src;
      iframe.title = this.dataset.title || '';
      iframe.allow =
        'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
      iframe.allowFullscreen = true;
      iframe.setAttribute('frameborder', '0');

      this.classList.add('is-active');
      this.appendChild(iframe);

      // Move focus into the player so keyboard users are not left on a button
      // that has just disappeared.
      iframe.addEventListener('load', () => iframe.focus({ preventScroll: true }), { once: true });
    }
  }

  window.Aurelia.define('video-facade', VideoFacade);

  /* ---------------------------------------------------------------------
     Viewport-aware native video
     --------------------------------------------------------------------- */
  class VideoSection extends Element {
    mount() {
      this.video = this.querySelector('video');
      if (!this.video) return;

      // A background video is decoration. If the visitor asks for reduced
      // motion, freeze it on its poster frame rather than playing it.
      if (motion.reduced && this.video.autoplay) {
        this.video.autoplay = false;
        this.video.pause();
        return;
      }

      if (!this.video.autoplay) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              // `play()` rejects if the browser blocks autoplay; that is an
              // expected outcome, not an error worth surfacing.
              const attempt = this.video.play();
              if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
            } else {
              this.video.pause();
            }
          });
        },
        { threshold: 0.15 }
      );

      this.observer.observe(this);

      // Pause when the tab is hidden: browsers do not always do this for
      // muted inline video, and it is a measurable battery cost.
      this.listen(document, 'visibilitychange', () => {
        if (document.hidden) this.video.pause();
      });
    }

    unmount() {
      if (this.observer) this.observer.disconnect();
    }
  }

  window.Aurelia.define('video-section', VideoSection);
})();
